import { createSlice, PayloadAction,createAsyncThunk } from '@reduxjs/toolkit';
import * as apiService from 'modules/flywheel/services/chatService';

interface Chat {
  conversation_id?: string;
  prompt: string;
  response: any[];
}

interface ChatState {
  capabilitiesList: any[];
  conversations: any[];
  categorizedConversations: Record<string, any>;
  chats: Chat[];
  isLoading: boolean;
  userFinalPrompt: string;
   workflowSteps: WorkflowStep[];
  workflowLoading: boolean;
  currentChats:Chat[];
}

const initialState: ChatState = {
  capabilitiesList: [],
  conversations: [],
  categorizedConversations: {},
  chats: [{ prompt: "", response: [] }],
  currentChats:[{ prompt: "", response: [] }],
  isLoading: false,
  userFinalPrompt: "",
  workflowSteps: [],
  workflowLoading: false
};
interface WorkflowStep {
  id: string;
  label: string;
}
export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setCapabilitiesList: (state, action: PayloadAction<any[]>) => {
      state.capabilitiesList = action.payload;
    },
    setConversations: (state, action: PayloadAction<any[]>) => {
      state.conversations = action.payload;
    },
    setCategorizedConversations: (state, action: PayloadAction<Record<string, any>>) => {
      state.categorizedConversations = action.payload;
    },
    setChats: (state, action: PayloadAction<Chat[]>) => {
      state.chats = action.payload;
      state.currentChats=action.payload;
    },
    addChat: (state, action: PayloadAction<Chat>) => {
      state.chats.push(action.payload);
      state.currentChats.push(action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    clearUserFinalPrompt: (state) => {
      state.userFinalPrompt = "";
    },
    
removeFromCurrentChats: (state, action: PayloadAction<string>) => {
  state.currentChats = state.currentChats.filter(
    chat => chat.conversation_id !== action.payload
  );
  
  // If currentChats becomes empty, initialize with empty chat
  if (state.currentChats.length === 0) {
    state.currentChats = [{ prompt: "", response: [] }];
  }
},
clearCurrentChats: (state) => {
  state.currentChats = [{ prompt: "", response: [] }];
}
  }
  ,extraReducers: (builder) => {
  builder
    .addCase(fetchCapabilitiesThunk.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(fetchCapabilitiesThunk.fulfilled, (state, action) => {
      state.capabilitiesList = action.payload;
      state.isLoading = false;
    })
    .addCase(fetchCapabilitiesThunk.rejected, (state) => {
      state.isLoading = false;
    })
    .addCase(fetchConversationsThunk.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(fetchConversationsThunk.fulfilled, (state, action) => {
      state.conversations = action.payload.conversations;
      state.categorizedConversations = action.payload.categorized;
      state.isLoading = false;
    })
    .addCase(fetchConversationsThunk.rejected, (state) => {
      state.isLoading = false;
    }).addCase(sendMessageThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendMessageThunk.fulfilled, (state, action) => {
        state.chats.push({
           conversation_id: action.meta.arg.conversationId,
          prompt: action.payload.prompt,
          response: action.payload.response
        });
         state.currentChats.push({
           conversation_id: action.meta.arg.conversationId,
          prompt: action.payload.prompt,
          response: action.payload.response
        });
        state.isLoading = false;
      })
      .addCase(sendMessageThunk.rejected, (state) => {
        state.isLoading = false;
      }).addCase(fetchWorkflowThunk.pending, (state) => {
    state.workflowLoading = true;
  })
  .addCase(fetchWorkflowThunk.fulfilled, (state, action) => {
    state.workflowSteps = action.payload;
    state.workflowLoading = false;
  })
  .addCase(fetchWorkflowThunk.rejected, (state) => {
    state.workflowLoading = false;
    state.workflowSteps = [];
  }).addCase(getMessagesByConvoIdThunk.fulfilled, (state, action) => {
 state.currentChats = Array.isArray(action.payload.chats)
    ? action.payload.chats
    : [action.payload.chats];

  state.isLoading = false;
})
}
});

export const {
  setCapabilitiesList,
  setConversations,
  setCategorizedConversations,
  setChats,
  addChat,
  setLoading,
  removeFromCurrentChats,
  clearCurrentChats
} = chatSlice.actions;
// Async thunks
export const fetchCapabilitiesThunk = createAsyncThunk(
  'chat/fetchCapabilities',
  async () => {
    return await apiService.fetchCapabilities();
  }
);

export const fetchConversationsThunk = createAsyncThunk(
  'chat/fetchConversations',
  async () => {
    const conversations = await apiService.fetchConversations();
    return {
      conversations,
      categorized: apiService.categorizeConversations(conversations)
    };
  }
);
export const sendMessageThunk = createAsyncThunk(
  'chat/sendMessage',
  async ({ userInput, activeAction, filePayload,conversationId }: {
    userInput: string,
    activeAction: string,
    conversationId:string,
    filePayload?: any
  }) => {
    let response: any[] = [];
    
    if (activeAction === "fileUpload" && filePayload) {
      response = [await apiService.uploadFile(userInput,conversationId, filePayload)];
    } else if (activeAction === "") {
      response = await apiService.sendMessage({userInput,conversationId});
    } else {
      response = await apiService.executeTask(activeAction);
    }
    
    return {
      prompt: userInput,
      response
    };
  }
);
export const fetchWorkflowThunk = createAsyncThunk(
  'chat/fetchWorkflow',
  async (userInput: string) => {
    const response = await apiService.fetchWorkflowSteps(userInput);
    if (response && response) {
      const steps: WorkflowStep[] = [];
      const regex = /- ✅ (.+)/g;
      let match;
      while ((match = regex.exec(response)) !== null) {
        steps.push({ id: `${steps.length + 1}`, label: match[1] });
      }
      return steps;
    }
    return [];
  }
);

export const getMessagesByConvoIdThunk = createAsyncThunk(
  'chat/getMessagesByConvoId',
  async (conversationId: string) => {
    const messages = await apiService.getMessageByConvoId(conversationId);
    return messages;
  }
);  
export default chatSlice.reducer;